import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTransferOperations } from '../hooks/useTransfers';
import { useBalances } from '../hooks/useBalances';
import useSWR from 'swr';
import apiClient, { markTransferComplete, updateTransferInitiatedBy, recheckTransfer } from '../services/apiClient';
import { useTimezone, formatTimestampWithTimezone } from '../contexts/TimezoneContext';
import { Copy, ChevronDown, Wallet, Clock, Info, ArrowLeftRight, History, Loader2, RotateCcw, RefreshCw } from 'lucide-react';
import { EXCHANGES, EXCHANGE_IDS, CHAINS, ASSETS, EXCHANGE_ACCOUNT_TYPES } from '../constants';
import { formatBalance, getExchangeBalances, getTreasuryBalances, getTreasuryWalletBalances, shortenAddress, isTreasuryWallet, getTreasuryWalletAddress } from '../utils/balanceUtils';
import { CancellationWindowTimer } from './transfers/CancellationWindowTimer';
import { TreasuryHoldTimer } from './transfers/TreasuryHoldTimer';
import { useTransferEvents } from '../hooks/useTransferEvents';
import SwapForm from './SwapForm';
import './TransfersPage.css';

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    console.log('Address copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
};

const AddressWithCopy = ({ address }) => {
  if (!address) return null;
  return (
    <div className="address-with-copy">
      <span className="mono">{shortenAddress(address, 8, 8)}</span>
      <button 
        className="btn-icon-small" 
        onClick={(e) => {
          e.stopPropagation();
          copyToClipboard(address);
        }}
        title="Copy address"
      >
        <Copy size={12} />
      </button>
    </div>
  );
};

const fetcher = (url) => apiClient.get(url).then(res => res.data);
const swrFetcher = (url) => apiClient.get(url).then(res => res.data);

function TransfersPage({ onTransferInitiated, onNavigateToHistory }) {
  const { submitTransfer, isSubmitting, error, clearError, cancel: cancelTransfer } = useTransferOperations();
  const { balances, refresh: refreshBalances, isValidating: isBalancesValidating } = useBalances();
  const { activeTimezone } = useTimezone();
  const isSubmittingRef = useRef(false); // Guard against double submissions
  const [fetchingBalanceFor, setFetchingBalanceFor] = useState(null); // Track which exchange/wallet is being fetched
  
  // Fetch treasury wallets
  const { data: treasuryWalletsData } = useSWR('/admin/treasury-wallets', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false,
  });
  
  // Fetch wallet metadata
  const { data: walletMetadataData } = useSWR('/admin/treasury-wallets/metadata', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false,
  });

  // Fetch whitelist for deposit addresses
  const { data: whitelistResponse } = useSWR('/admin/whitelist', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: true,
    revalidateOnMount: true,
  });

  // Fetch configured exchanges (exchanges with credentials)
  const { data: exchangesResponse } = useSWR('/admin/exchanges', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: true,
    revalidateOnMount: true,
  });

  // Transform whitelist data into exchanges structure
  const whitelistData = useMemo(() => {
    if (!whitelistResponse?.wallets) return { exchanges: {} };
    
    const wallets = whitelistResponse.wallets;
    const exchanges = {};
    
    // Group wallets by exchange_id where wallet_type is 'deposit'
    wallets.forEach((wallet) => {
      if (wallet.exchange_id && wallet.wallet_type === 'deposit' && wallet.address) {
        const exchangeId = wallet.exchange_id.toLowerCase();
        if (!exchanges[exchangeId]) {
          exchanges[exchangeId] = { depositAddresses: {} };
        }
        if (wallet.chain) {
          exchanges[exchangeId].depositAddresses[wallet.chain] = wallet.address;
        }
      }
    });
    
    return { exchanges };
  }, [whitelistResponse]);

  // Get list of linked exchanges (exchanges with at least one deposit address OR configured credentials)
  const linkedExchanges = useMemo(() => {
    const linked = new Set();
    
    // Add exchanges with deposit addresses (only valid exchange IDs)
    if (whitelistData?.exchanges) {
      Object.keys(whitelistData.exchanges).forEach(exchangeId => {
        const normalizedId = exchangeId.toLowerCase();
        // Only add if it's a valid exchange ID
        if (EXCHANGE_IDS.includes(normalizedId)) {
          const exchange = whitelistData.exchanges[exchangeId];
          if (exchange?.depositAddresses && Object.keys(exchange.depositAddresses).length > 0) {
            linked.add(normalizedId);
          }
        }
      });
    }
    
    // Add exchanges with configured credentials (even if no deposit address yet)
    // IMPORTANT: Only include valid exchange IDs to prevent invalid data
    if (exchangesResponse?.exchanges) {
      exchangesResponse.exchanges.forEach(exchange => {
        const exchangeId = exchange.exchangeId?.toLowerCase();
        // Validate: Only process if it's a known exchange ID
        if (exchangeId && EXCHANGE_IDS.includes(exchangeId) && exchange.hasApiKey && exchange.isEnabled !== false) {
          linked.add(exchangeId);
        }
      });
    }
    
    return linked;
  }, [whitelistData, exchangesResponse]);

  // Get available exchanges list (from configured exchanges, with metadata from constants)
  const availableExchanges = useMemo(() => {
    const exchangesMap = new Map();
    
    // Start with all exchanges from constants (for metadata like hasSubAccounts)
    EXCHANGES.forEach(ex => {
      exchangesMap.set(ex.id, ex);
    });
    
    // Update with configured exchanges from API
    // IMPORTANT: Only include valid exchange IDs to prevent environment variables or invalid data from appearing
    if (exchangesResponse?.exchanges) {
      exchangesResponse.exchanges.forEach(exchange => {
        const exchangeId = exchange.exchangeId?.toLowerCase();
        // Validate: Only process if it's a known exchange ID
        if (exchangeId && EXCHANGE_IDS.includes(exchangeId) && exchange.hasApiKey && exchange.isEnabled !== false) {
          // Use metadata from constants if available
          const existing = exchangesMap.get(exchangeId);
          if (existing) {
            exchangesMap.set(exchangeId, existing);
          }
        }
      });
    }
    
    // Return only exchanges that are linked (have credentials or deposit addresses) AND are valid exchange IDs
    return Array.from(exchangesMap.values()).filter(ex => 
      linkedExchanges.has(ex.id) && EXCHANGE_IDS.includes(ex.id)
    );
  }, [exchangesResponse, linkedExchanges]);
  
  // Track SSE connection state using ref (for use in refreshInterval callback)
  const isSSEConnectedRef = useRef(false);

  // Fetch all recent transfers
  // When SSE is connected: use slower polling (30-60s) as safety net
  // When SSE is disconnected: use faster polling (2-5s) as primary method
  const { data: transfersData, mutate: mutateTransfers, error: transfersError, isValidating } = useSWR('/transfers/history?limit=20', fetcher, {
    onSuccess: (data) => {
      // #region agent log
      const inTreasuryTransfers = data?.transfers?.filter(t => t.status?.toLowerCase() === 'in_treasury') || [];
      inTreasuryTransfers.forEach(t => {
        fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:172',message:'API response received for IN_TREASURY transfer',data:{transferId:t.id,status:t.status,hasTreasuryReceivedAt:!!(t.treasuryReceivedAt||t.treasury_received_at),hasTimers:!!t.timers,hasTreasuryHold:!!t.timers?.treasuryHold,holdTimeSeconds:t.timers?.treasuryHold?.holdTimeSeconds,hasTreasuryCountdown:!!t.treasuryCountdown},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      });
      // #endregion
    },
    refreshInterval: (latestData) => {
      // Check if there are active transfers that might need faster updates
      // Use robust status extraction to handle null/undefined/whitespace
      const hasActiveTransfers = latestData?.transfers?.some(t => {
        const rawStatus = t.status || t.Status || t.transfer_status || null;
        if (!rawStatus || typeof rawStatus !== 'string') return false;
        const status = rawStatus.trim().toLowerCase();
        return ['pending', 'validated', 'withdrawing', 'swapping', 'depositing', 'in_progress', 'in_treasury'].includes(status);
      });
      
      // If SSE is connected, use slower polling (30-60s) as a safety net
      // This ensures we still get updates even if SSE events are missed
      if (isSSEConnectedRef.current) {
        const interval = hasActiveTransfers ? 30000 : 60000; // 30s for active, 60s for inactive
        return interval;
      }
      
      // Fallback polling when SSE is disconnected - use faster polling
      const interval = hasActiveTransfers ? 2000 : 5000; // Poll every 2-5 seconds as fallback
      return interval;
    },
    revalidateOnFocus: true, // Revalidate when window regains focus
    revalidateOnReconnect: true, // Revalidate when network reconnects
    dedupingInterval: 1000, // Reduce dedupe window to 1s for faster updates
  });

  // Handle real-time transfer updates via SSE
  const handleTransferUpdate = useMemo(() => {
    return (eventData) => {
      
      // Handle reconnection event - immediately refresh to catch missed events
      if (eventData.type === 'reconnected' && eventData.refresh) {
        console.log('[TransfersPage] SSE reconnected - refreshing all data');
        mutateTransfers();
        refreshBalances();
        return;
      }
      
      if (eventData.transferId && eventData.status) {
        console.log('[TransfersPage] SSE transfer update:', eventData);
        console.log('[TransfersPage] SSE event treasuryHoldTimeSeconds:', eventData.treasuryHoldTimeSeconds);
        
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:211',message:'SSE event received',data:{transferId:eventData.transferId,status:eventData.status,hasTreasuryReceivedAt:!!eventData.treasuryReceivedAt,hasTreasuryHoldTimeSeconds:!!eventData.treasuryHoldTimeSeconds,treasuryHoldTimeSeconds:eventData.treasuryHoldTimeSeconds,isInTreasury:eventData.status?.toLowerCase()==='in_treasury'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion
        
        // CRITICAL: For IN_TREASURY transitions, immediately update local state with treasuryReceivedAt
        // This ensures the countdown timer appears immediately without waiting for API refetch
        if (eventData.status?.toLowerCase() === 'in_treasury' && eventData.treasuryReceivedAt) {
          console.log('[TransfersPage] Updating transfer optimistically with treasury data:', {
            transferId: eventData.transferId,
            treasuryReceivedAt: eventData.treasuryReceivedAt,
            treasuryHoldTimeSeconds: eventData.treasuryHoldTimeSeconds,
          });
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:217',message:'Starting optimistic update for IN_TREASURY',data:{transferId:eventData.transferId,treasuryReceivedAt:eventData.treasuryReceivedAt,treasuryHoldTimeSeconds:eventData.treasuryHoldTimeSeconds},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          
          // Optimistically update the transfer in the SWR cache
          mutateTransfers((currentData) => {
            if (!currentData?.transfers) return currentData;
            
            const updatedTransfers = currentData.transfers.map((transfer) => {
              if (transfer.id === eventData.transferId) {
                const holdTimeSeconds = eventData.treasuryHoldTimeSeconds;
                
                if (!holdTimeSeconds) {
                  console.warn('[TransfersPage] SSE event missing treasuryHoldTimeSeconds, will use API value on refetch');
                }
                
                return {
                  ...transfer,
                  status: 'in_treasury',
                  treasuryReceivedAt: eventData.treasuryReceivedAt,
                  treasury_received_at: eventData.treasuryReceivedAt,
                  // Update timers if hold time is provided
                  timers: holdTimeSeconds ? {
                    ...transfer.timers,
                    treasuryHold: {
                      receivedAt: eventData.treasuryReceivedAt,
                      holdTimeSeconds: holdTimeSeconds,
                      remainingSeconds: holdTimeSeconds, // Start with full time
                      isActive: true,
                      scheduledAt: new Date(new Date(eventData.treasuryReceivedAt).getTime() + holdTimeSeconds * 1000).toISOString(),
                    },
                  } : transfer.timers,
                  // Legacy format for backward compatibility
                  treasuryCountdown: holdTimeSeconds ? {
                    treasuryReceivedAt: eventData.treasuryReceivedAt,
                    depositScheduledAt: new Date(new Date(eventData.treasuryReceivedAt).getTime() + holdTimeSeconds * 1000).toISOString(),
                    remainingSeconds: holdTimeSeconds,
                    holdTimeSeconds: holdTimeSeconds,
                  } : transfer.treasuryCountdown,
                };
              }
              return transfer;
            });
            
            // #region agent log
            const updatedTransfer = updatedTransfers.find(t => t.id === eventData.transferId);
            fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:268',message:'Optimistic update completed',data:{transferId:eventData.transferId,updatedStatus:updatedTransfer?.status,hasTreasuryReceivedAt:!!updatedTransfer?.treasuryReceivedAt,hasTreasuryHold:!!updatedTransfer?.timers?.treasuryHold,holdTimeSeconds:updatedTransfer?.timers?.treasuryHold?.holdTimeSeconds},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
            // #endregion
            
            return {
              ...currentData,
              transfers: updatedTransfers,
            };
          }, false); // false = don't revalidate, we're updating optimistically
          
          // Also trigger a revalidation in the background to get full data
          // But use revalidate: true to ensure we get the latest data from the API
          // The optimistic update above ensures the UI shows the correct value immediately
          mutateTransfers(undefined, { revalidate: true });
        } else {
          // For other status changes, just trigger revalidation
          mutateTransfers();
        }
      }
    };
  }, [mutateTransfers, refreshBalances]);

  // Connect to SSE stream for real-time updates
  const { isConnected: isSSEConnected } = useTransferEvents(handleTransferUpdate);
  
  // Update ref when SSE connection state changes
  useEffect(() => {
    isSSEConnectedRef.current = isSSEConnected;
  }, [isSSEConnected]);

  // Fetch cancellation window setting
  const { data: cancellationWindowSetting } = useSWR('/admin/secrets/CANCELLATION_WINDOW_SECONDS', swrFetcher, {
    refreshInterval: 0,
    revalidateOnFocus: false,
  });
  
  // Get cancellation window duration (default: 12 seconds)
  const cancellationWindowDuration = useMemo(() => {
    if (cancellationWindowSetting?.preview) {
      const seconds = parseInt(cancellationWindowSetting.preview, 10);
      if (!isNaN(seconds) && seconds >= 0 && seconds <= 60) {
        return seconds;
      }
    }
    // Default to 12 seconds if not configured
    return 12;
  }, [cancellationWindowSetting]);

  // Countdown state for treasury hold timer and cancellation window
  const [countdownTick, setCountdownTick] = useState(0);

  // Filter transfers by status (backend uses lowercase values)
  const activeTransfers = useMemo(() => {
    if (!transfersData?.transfers) {
      console.log('[TransfersPage] No transfers data:', transfersData);
      return [];
    }
    
    // DIAGNOSTIC: Log raw transfer data structure to debug prod issues
    const diagnosticInfo = transfersData.transfers.slice(0, 3).map(t => ({
      id: t.id?.slice(0, 8),
      status: t.status,
      statusType: typeof t.status,
      statusLength: t.status?.length,
      hasStatus: 'status' in t,
      keys: Object.keys(t).filter(k => k.toLowerCase().includes('status')),
      errorMessage: t.errorMessage || t.error_message,
      rawTransfer: JSON.stringify(t).substring(0, 200) // First 200 chars for debugging
    }));
    console.log('[TransfersPage] DIAGNOSTIC - Transfer data structure:', diagnosticInfo);
    console.log('[TransfersPage] Processing transfers:', {
      total: transfersData.transfers.length,
      statuses: transfersData.transfers.map(t => ({ 
        id: t.id?.slice(0, 8), 
        status: t.status,
        statusTrimmed: t.status?.trim(),
        statusLower: t.status?.toLowerCase()?.trim()
      }))
    });
    
    // Active = pending, validated, withdrawing, in_treasury, depositing, in_progress
    // CRITICAL: Exclude cancelled, failed, completed, rejected transfers
    const activeStatuses = ['pending', 'validated', 'withdrawing', 'in_treasury', 'swapping', 'depositing', 'in_progress'];
    const terminalStatuses = ['cancelled', 'failed', 'completed', 'rejected'];
    
    const filtered = transfersData.transfers.filter(t => {
      // Robust status extraction: try multiple field names and handle null/undefined
      const rawStatus = t.status || t.Status || t.transfer_status || null;
      
      if (!rawStatus || typeof rawStatus !== 'string') {
        console.warn('[TransfersPage] Transfer missing status field:', {
          id: t.id?.slice(0, 8),
          hasStatus: 'status' in t,
          hasStatusUpper: 'Status' in t,
          keys: Object.keys(t)
        });
        return false;
      }
      
      // Trim whitespace and convert to lowercase
      const statusLower = rawStatus.trim().toLowerCase();
      const isActive = activeStatuses.includes(statusLower);
      const isTerminal = terminalStatuses.includes(statusLower);
      
      // Check error messages (try both camelCase and snake_case)
      const errorMsg = (t.errorMessage || t.error_message || '').toLowerCase();
      const hasErrorCancelled = errorMsg.includes('cancelled');
      
      // Exclude terminal statuses and transfers with "cancelled" in error message
      if (isTerminal || hasErrorCancelled) {
        return false;
      }
      
      // Log if we have an active status that's not matching
      if (!isActive && !isTerminal && statusLower) {
        console.log('[TransfersPage] Unknown status detected:', {
          id: t.id?.slice(0, 8),
          rawStatus,
          statusLower,
          isActive,
          isTerminal
        });
      }
      
      return isActive;
    });
    
    console.log('[TransfersPage] Active transfers after filtering:', {
      count: filtered.length,
      ids: filtered.map(t => t.id?.slice(0, 8)),
      statuses: filtered.map(t => {
        const rawStatus = t.status || t.Status || t.transfer_status || 'UNKNOWN';
        return rawStatus.trim().toLowerCase();
      })
    });
    
    return filtered;
  }, [transfersData]);
  
  const recentTransfers = useMemo(() => {
    if (!transfersData?.transfers) return [];
    
    // Show both completed, failed, cancelled, and rejected transfers in recent list
    const terminalStatuses = ['completed', 'failed', 'cancelled', 'rejected'];
    const recent = transfersData.transfers
      .filter(t => {
        // Robust status extraction: try multiple field names and handle null/undefined
        const rawStatus = t.status || t.Status || t.transfer_status || null;
        if (!rawStatus || typeof rawStatus !== 'string') {
          return false;
        }
        const statusLower = rawStatus.trim().toLowerCase();
        return terminalStatuses.includes(statusLower);
      })
      .slice(0, 5);
    
    console.log('[TransfersPage] Recent transfers:', {
      count: recent.length,
      ids: recent.map(t => t.id?.slice(0, 8)),
      statuses: recent.map(t => {
        const rawStatus = t.status || t.Status || t.transfer_status || 'UNKNOWN';
        return rawStatus.trim().toLowerCase();
      })
    });
    
    return recent;
  }, [transfersData]);
  
  // Countdown state update - runs when there are active transfers
  useEffect(() => {
    const hasInTreasury = activeTransfers?.some(t => t.status?.toLowerCase() === 'in_treasury');
    const hasActiveTransfers = activeTransfers && activeTransfers.length > 0;
    
    // Run interval if there are active transfers (for cancellation window) or treasury transfers
    if (!hasInTreasury && !hasActiveTransfers) {
      return;
    }
    
    const interval = setInterval(() => {
      setCountdownTick(tick => tick + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [activeTransfers]);

  // Helper to calculate remaining countdown seconds for treasury hold
  // Now uses backend-provided timer (authoritative) with fallback for backward compatibility
  const getCountdownSeconds = (transfer) => {
    if (transfer.status?.toLowerCase() !== 'in_treasury') {
      return null;
    }
    
    // PREFERRED: Use backend-provided timer (authoritative, server time)
    // CRITICAL: Don't adjust for elapsed time client-side - backend already uses server time
    // Adjusting causes timezone issues (UTC vs local time mismatch)
    if (transfer.timers?.treasuryHold?.remainingSeconds !== undefined) {
      // Use backend value directly - it's already calculated correctly with server time
      return Math.max(0, transfer.timers.treasuryHold.remainingSeconds);
    }
    
    // FALLBACK 1: Use legacy treasuryCountdown format (backward compatibility)
    if (transfer.treasuryCountdown?.remainingSeconds !== undefined) {
      // Use backend value directly - don't adjust for elapsed time
      return Math.max(0, transfer.treasuryCountdown.remainingSeconds);
    }
    
    // FALLBACK 2: Calculate from treasuryReceivedAt (last resort)
    const receivedAt = transfer.treasuryReceivedAt || transfer.treasury_received_at;
    if (!receivedAt) {
      return null;
    }
    
    // Use backend-provided holdTimeSeconds (check both new and legacy formats)
    const holdTimeSeconds = transfer.timers?.treasuryHold?.holdTimeSeconds 
      || transfer.treasuryCountdown?.holdTimeSeconds 
      || 60; // Fallback only if backend doesn't provide it
    const HOLD_TIME_MS = holdTimeSeconds * 1000;
    const receivedTime = new Date(receivedAt).getTime();
    const depositTime = receivedTime + HOLD_TIME_MS;
    const remaining = Math.max(0, Math.ceil((depositTime - Date.now()) / 1000));
    return remaining;
  };

  const [formData, setFormData] = useState({
    transferType: 'exchange-to-exchange',
    sourceExchange: '',
    sourceSubAccount: 'main',
    sourceAccountType: '', // Account type for same-exchange internal transfers (spot, futures, margin, funding, etc.)
    sourceChain: 'arbitrum', // Chain for treasury wallet source
    destinationExchange: '',
    destinationSubAccount: 'main',
    destinationAccountType: '', // Account type for same-exchange internal transfers
    destinationChain: 'arbitrum', // Chain for treasury wallet destination
    asset: 'USDT',
    amount: '',
    chain: 'arbitrum',
    treasuryWallet: '', // Will be set to primary wallet address
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTreasurySelector, setShowTreasurySelector] = useState(false);
  const [mobileView, setMobileView] = useState('form'); // 'form' or 'transfers'
  const [activeTab, setActiveTab] = useState('transfer'); // 'transfer' or 'swap'

  // Close treasury selector when clicking outside
  useEffect(() => {
    if (!showTreasurySelector) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.treasury-selector-wrapper')) {
        setShowTreasurySelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTreasurySelector]);

  // Build list of available treasury wallets
  const treasuryWalletsList = useMemo(() => {
    const configuredByAddress = treasuryWalletsData?.configuredByAddress || {};
    const configuredByChain = treasuryWalletsData?.configured || {}; // Old format
    const envAddress = treasuryWalletsData?.envWalletAddress?.toLowerCase();
    const defaultWallet = treasuryWalletsData?.defaultWallet?.toLowerCase() || envAddress;
    const walletMetadata = walletMetadataData || {};
    
    const walletsMap = new Map();
    
    // Process new format: address -> {chains, type}
    if (Object.keys(configuredByAddress).length > 0) {
      Object.entries(configuredByAddress).forEach(([address, wallet]) => {
        const addr = address.toLowerCase();
        walletsMap.set(addr, {
          address: address,
          name: walletMetadata[addr]?.name || '',
          chains: [...(wallet.chains || [])],
          type: wallet.type || 'eoa',
          isPrimary: addr === envAddress,
          isDefault: addr === defaultWallet,
          isMain: walletMetadata[addr]?.isMain || (addr === defaultWallet),
        });
      });
    } else {
      // Fallback to old format
      Object.entries(configuredByChain).forEach(([chain, wallet]) => {
        const addr = wallet.address?.toLowerCase();
        if (!addr) return;
        
        if (!walletsMap.has(addr)) {
          walletsMap.set(addr, {
            address: wallet.address,
            name: walletMetadata[addr]?.name || '',
            chains: [],
            type: wallet.type || 'eoa',
            isPrimary: addr === envAddress,
            isDefault: addr === defaultWallet,
            isMain: walletMetadata[addr]?.isMain || (addr === defaultWallet),
          });
        }
        walletsMap.get(addr).chains.push(chain);
      });
    }
    
    // Add primary wallet if not in configured
    if (envAddress && !walletsMap.has(envAddress)) {
      walletsMap.set(envAddress, {
        address: treasuryWalletsData.envWalletAddress,
        name: walletMetadata[envAddress]?.name || '',
        chains: ['arbitrum'],
        type: 'eoa',
        isPrimary: true,
        isDefault: envAddress === defaultWallet,
        isMain: walletMetadata[envAddress]?.isMain || (envAddress === defaultWallet),
      });
    }
    
    return Array.from(walletsMap.values()).map(wallet => {
      const addr = wallet.address.toLowerCase();
      return {
        ...wallet,
        isIntermediary: walletMetadata[addr]?.isIntermediary || false,
        hasPrivateKey: walletMetadata[addr]?.hasPrivateKey || false,
        label: wallet.isMain 
          ? wallet.name || 'Main Wallet'
          : wallet.isDefault 
            ? wallet.name || 'Default Wallet'
            : wallet.isPrimary 
              ? wallet.name || 'Primary Wallet' 
              : wallet.name || `${wallet.type.toUpperCase()} Wallet`,
      };
    });
  }, [treasuryWalletsData, walletMetadataData]);

  // Filter wallets: intermediary wallets (for selector) and all wallets (for dropdown)
  const intermediaryWalletsList = useMemo(() => {
    return treasuryWalletsList.filter(w => w.isIntermediary && w.hasPrivateKey);
  }, [treasuryWalletsList]);

  const regularWalletsList = useMemo(() => {
    return treasuryWalletsList.filter(w => !w.isIntermediary);
  }, [treasuryWalletsList]);

  // Set default intermediary wallet
  useEffect(() => {
    if (!formData.treasuryWallet && intermediaryWalletsList.length > 0) {
      const defaultWallet = intermediaryWalletsList.find(w => w.isMain) 
        || intermediaryWalletsList.find(w => w.isDefault) 
        || intermediaryWalletsList.find(w => w.isPrimary)
        || intermediaryWalletsList[0];
      
      if (defaultWallet) {
        setFormData(prev => ({ ...prev, treasuryWallet: defaultWallet.address }));
      }
    }
  }, [intermediaryWalletsList]);

  const selectedTreasuryWallet = useMemo(() => {
    return intermediaryWalletsList.find(w => w.address === formData.treasuryWallet) || intermediaryWalletsList[0];
  }, [intermediaryWalletsList, formData.treasuryWallet]);

  const sourceBalances = useMemo(() => {
    if (!formData.sourceExchange) return null;
    if (isTreasuryWallet(formData.sourceExchange)) {
      const walletAddress = getTreasuryWalletAddress(formData.sourceExchange);
      return getTreasuryWalletBalances(balances, walletAddress, formData.sourceChain);
    }
    return getExchangeBalances(balances, formData.sourceExchange);
  }, [balances, formData.sourceExchange, formData.sourceChain]);

  const destBalances = useMemo(() => {
    if (!formData.destinationExchange) return null;
    if (isTreasuryWallet(formData.destinationExchange)) {
      const walletAddress = getTreasuryWalletAddress(formData.destinationExchange);
      return getTreasuryWalletBalances(balances, walletAddress, formData.destinationChain);
    }
    return getExchangeBalances(balances, formData.destinationExchange);
  }, [balances, formData.destinationExchange, formData.destinationChain]);

  const treasuryBalances = useMemo(() => {
    return getTreasuryBalances(balances);
  }, [balances]);

  // Calculate equalize amount
  const equalizeAmount = useMemo(() => {
    if (!sourceBalances || !destBalances || !formData.asset) return null;
    const sourceBal = parseFloat(sourceBalances[formData.asset]?.available || 0);
    const destBal = parseFloat(destBalances[formData.asset]?.available || 0);
    if (sourceBal === 0 && destBal === 0) return null;
    const avg = (sourceBal + destBal) / 2;
    const diff = sourceBal - avg;
    if (Math.abs(diff) < 1) return null; // Already balanced
    return Math.abs(diff);
  }, [sourceBalances, destBalances, formData.asset]);

  // Function to populate form from a transfer
  const populateFormFromTransfer = (transfer) => {
    const sourceExchange = transfer.source?.exchange || transfer.source_exchange || '';
    const destExchange = transfer.destination?.exchange || transfer.destination_exchange || '';
    const amount = transfer.source?.amount || transfer.amount || '';
    const asset = transfer.source?.asset || transfer.asset || 'USDT';
    const chain = transfer.source?.chain || transfer.chain || 'arbitrum';
    
    // Determine if source/dest are treasury wallets
    const sourceIsWallet = sourceExchange?.startsWith('0x');
    const destIsWallet = destExchange?.startsWith('0x');
    
    // Map wallet addresses to treasury:address format
    const sourceExchangeValue = sourceIsWallet ? `treasury:${sourceExchange}` : sourceExchange;
    const destExchangeValue = destIsWallet ? `treasury:${destExchange}` : destExchange;
    
    // Determine transfer type
    let transferType = 'exchange-to-exchange';
    if (sourceIsWallet && destIsWallet) {
      transferType = 'treasury-to-treasury';
    } else if (sourceIsWallet && !destIsWallet) {
      transferType = 'treasury-to-exchange';
    } else if (!sourceIsWallet && destIsWallet) {
      transferType = 'exchange-to-treasury';
    }
    
    // Get wallet chains if applicable
    let sourceChain = chain;
    let destChain = chain;
    if (sourceIsWallet) {
      const sourceWallet = treasuryWalletsList.find(w => w.address.toLowerCase() === sourceExchange.toLowerCase());
      if (sourceWallet && sourceWallet.chains.length > 0) {
        sourceChain = sourceWallet.chains[0];
      }
    }
    if (destIsWallet) {
      const destWallet = treasuryWalletsList.find(w => w.address.toLowerCase() === destExchange.toLowerCase());
      if (destWallet && destWallet.chains.length > 0) {
        destChain = destWallet.chains[0];
      }
    }
    
    // Auto-select USDC for Hyperliquid/Extended source (they only support USDC)
    const finalAsset = (sourceExchangeValue === 'hyperliquid' || sourceExchangeValue === 'extended') ? 'USDC' : asset;
    
    setFormData({
      transferType,
      sourceExchange: sourceExchangeValue,
      sourceSubAccount: 'main',
      sourceChain,
      destinationExchange: destExchangeValue,
      destinationSubAccount: 'main',
      destinationChain: destChain,
      asset: finalAsset,
      amount: amount.toString(),
      chain,
      treasuryWallet: formData.treasuryWallet, // Keep existing treasury wallet selection
    });
    
    // Scroll to form
    const formElement = document.querySelector('.transfer-form-container');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Auto-fetch balances when exchange/wallet is selected
  useEffect(() => {
    if (formData.sourceExchange) {
      setFetchingBalanceFor('source');
      refreshBalances().finally(() => {
        // Clear fetching state after a short delay to show spinner
        setTimeout(() => setFetchingBalanceFor(null), 500);
      });
    }
  }, [formData.sourceExchange, formData.sourceChain, refreshBalances]);

  useEffect(() => {
    if (formData.destinationExchange) {
      setFetchingBalanceFor('destination');
      refreshBalances().finally(() => {
        // Clear fetching state after a short delay to show spinner
        setTimeout(() => setFetchingBalanceFor(null), 500);
      });
    }
  }, [formData.destinationExchange, formData.destinationChain, refreshBalances]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // When a treasury wallet is selected, set default chain to first available chain
    if (name === 'sourceExchange' && isTreasuryWallet(value)) {
      const walletAddress = getTreasuryWalletAddress(value);
      const wallet = treasuryWalletsList.find(w => w.address === walletAddress);
      if (wallet && wallet.chains.length > 0) {
        setFormData((prev) => ({ 
          ...prev, 
          [name]: value,
          sourceChain: wallet.chains[0] // Set to first available chain
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === 'destinationExchange' && isTreasuryWallet(value)) {
      const walletAddress = getTreasuryWalletAddress(value);
      const wallet = treasuryWalletsList.find(w => w.address === walletAddress);
      if (wallet && wallet.chains.length > 0) {
        setFormData((prev) => ({ 
          ...prev, 
          [name]: value,
          destinationChain: wallet.chains[0] // Set to first available chain
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === 'sourceExchange' && (value === 'hyperliquid' || value === 'extended')) {
      // Auto-select USDC when Hyperliquid/Extended is source (they only support USDC)
      setFormData((prev) => ({ 
        ...prev, 
        [name]: value,
        asset: 'USDC'
      }));
    } else {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        
        // When exchange changes, clear account types if they're no longer valid
        if (name === 'sourceExchange' || name === 'destinationExchange') {
          const sourceEx = name === 'sourceExchange' ? value : prev.sourceExchange;
          const destEx = name === 'destinationExchange' ? value : prev.destinationExchange;
          const isSameExchange = sourceEx === destEx && 
                                 !isTreasuryWallet(sourceEx) && 
                                 !isTreasuryWallet(destEx);
          
          if (!isSameExchange) {
            // Clear account types if exchanges differ
            updated.sourceAccountType = '';
            updated.destinationAccountType = '';
          }
        }
        
        return updated;
      });
    }
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (error) clearError();
    
    // Note: Balance refresh is now handled by useEffect hooks above
    // This ensures balances are fetched automatically when exchange/wallet changes
  };

  const handleSetMax = () => {
    if (sourceBalances?.[formData.asset]?.available) {
      setFormData((prev) => ({ 
        ...prev, 
        amount: sourceBalances[formData.asset].available.toString() 
      }));
    }
  };

  const handleEqualize = () => {
    if (equalizeAmount) {
      setFormData((prev) => ({ 
        ...prev, 
        amount: equalizeAmount.toString() 
      }));
    }
  };

  const handleReverse = () => {
    setFormData((prev) => ({
      ...prev,
      sourceExchange: prev.destinationExchange,
      destinationExchange: prev.sourceExchange,
      sourceSubAccount: prev.destinationSubAccount,
      destinationSubAccount: prev.sourceSubAccount,
      sourceAccountType: prev.destinationAccountType,
      destinationAccountType: prev.sourceAccountType,
      sourceChain: prev.destinationChain,
      destinationChain: prev.sourceChain,
    }));
    // Clear validation errors when reversing
    setValidationErrors({});
  };

  const handleCancel = () => {
    setFormData({
      transferType: 'exchange-to-exchange',
      sourceExchange: '',
      sourceSubAccount: 'main',
      sourceAccountType: '',
      sourceChain: 'arbitrum',
      destinationExchange: '',
      destinationSubAccount: 'main',
      destinationAccountType: '',
      destinationChain: 'arbitrum',
      asset: 'USDT',
      amount: '',
      chain: 'arbitrum',
      treasuryWallet: formData.treasuryWallet, // Keep treasury wallet selection
    });
    setValidationErrors({});
    if (error) clearError();
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.sourceExchange) errors.sourceExchange = 'Required';
    if (!formData.destinationExchange) errors.destinationExchange = 'Required';
    
    // Check if same exchange - if so, account types must be different
    const isSameExchange = formData.sourceExchange === formData.destinationExchange && 
                          !isTreasuryWallet(formData.sourceExchange) && 
                          !isTreasuryWallet(formData.destinationExchange);
    
    if (isSameExchange) {
      // For same-exchange transfers, account types are required and must differ
      if (!formData.sourceAccountType || !formData.destinationAccountType) {
        errors.destinationExchange = 'Account types required for internal transfers';
      } else if (formData.sourceAccountType === formData.destinationAccountType) {
        errors.destinationExchange = 'Account types must differ for internal transfers';
      }
    } else if (formData.sourceExchange === formData.destinationExchange && 
               (isTreasuryWallet(formData.sourceExchange) || isTreasuryWallet(formData.destinationExchange))) {
      // Treasury wallets can't transfer to themselves
      errors.destinationExchange = 'Must differ';
    }
    const amount = parseFloat(formData.amount);
    if (!formData.amount || amount <= 0) errors.amount = 'Invalid';
    else if (sourceBalances?.[formData.asset] && amount > parseFloat(sourceBalances[formData.asset].available || 0)) {
      errors.amount = 'Insufficient';
    }
    // Treasury wallets don't support sub-accounts
    if (isTreasuryWallet(formData.sourceExchange) && formData.sourceSubAccount !== 'main') {
      errors.sourceSubAccount = 'Treasury wallets do not support sub-accounts';
    }
    if (isTreasuryWallet(formData.destinationExchange) && formData.destinationSubAccount !== 'main') {
      errors.destinationSubAccount = 'Treasury wallets do not support sub-accounts';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) setShowConfirmation(true);
  };

  const handleSubmit = async () => {
    // Prevent double submissions
    if (isSubmittingRef.current || isSubmitting) {
      console.warn('Transfer submission already in progress, ignoring duplicate request');
      return;
    }
    
    isSubmittingRef.current = true;
    
    try {
      // Generate requestId for idempotency
      const requestId = crypto.randomUUID();

      // Extract exchange IDs or wallet addresses
      const sourceId = isTreasuryWallet(formData.sourceExchange) 
        ? getTreasuryWalletAddress(formData.sourceExchange)
        : formData.sourceExchange;
      const destId = isTreasuryWallet(formData.destinationExchange)
        ? getTreasuryWalletAddress(formData.destinationExchange)
        : formData.destinationExchange;
      
      // Build payload in the format backend expects:
      // source: { exchange, asset, amount, chain, accountType? }
      // destination: { exchange, asset?, chain?, accountType? }
      // For exchange-to-exchange transfers, include the intermediate treasury wallet
      const isExchangeToExchange = !isTreasuryWallet(formData.sourceExchange) && !isTreasuryWallet(formData.destinationExchange);
      const isSameExchange = formData.sourceExchange === formData.destinationExchange && isExchangeToExchange;
      
      const payload = {
        requestId,
        priority: 'normal',
        initiatedBy: 'gui', // Mark as GUI-initiated transfer
        // Include the selected treasury wallet for exchange-to-exchange transfers (but not for same-exchange internal transfers)
        ...(isExchangeToExchange && !isSameExchange && formData.treasuryWallet && {
          treasuryWallet: formData.treasuryWallet,
        }),
        source: {
          exchange: sourceId,
          asset: formData.asset,
          amount: parseFloat(formData.amount), // Backend expects number, not string
          chain: isTreasuryWallet(formData.sourceExchange) ? formData.sourceChain : formData.chain,
          ...(isTreasuryWallet(formData.sourceExchange) && {
            treasuryWallet: getTreasuryWalletAddress(formData.sourceExchange),
          }),
          // Include accountType for all transfers (not just same-exchange)
          ...(!isTreasuryWallet(formData.sourceExchange) && formData.sourceAccountType && {
            accountType: formData.sourceAccountType,
          }),
        },
        destination: {
          exchange: destId,
          asset: formData.asset, // Default to same asset
          chain: isTreasuryWallet(formData.destinationExchange) ? formData.destinationChain : formData.chain,
          ...(isTreasuryWallet(formData.destinationExchange) && {
            treasuryWallet: getTreasuryWalletAddress(formData.destinationExchange),
          }),
          // Include accountType for all transfers (not just same-exchange)
          ...(!isTreasuryWallet(formData.destinationExchange) && formData.destinationAccountType && {
            accountType: formData.destinationAccountType,
          }),
        },
      };
      
      
      const result = await submitTransfer(payload);
      
      setFormData({
        transferType: 'exchange-to-exchange',
        sourceExchange: '',
        sourceSubAccount: 'main',
        sourceAccountType: '',
        sourceChain: 'arbitrum',
        destinationExchange: '',
        destinationSubAccount: 'main',
        destinationAccountType: '',
        destinationChain: 'arbitrum',
        asset: 'USDT',
        amount: '',
        chain: 'arbitrum',
        treasuryWallet: formData.treasuryWallet, // Keep treasury wallet selection
      });
      setShowConfirmation(false);
      
      // Force immediate refresh of transfers list to show new transfer
      console.log('[TransfersPage] Transfer created, forcing immediate refresh');
      mutateTransfers(undefined, { revalidate: true });
      
      // Also refresh after a short delay to catch any race conditions
      setTimeout(() => {
        console.log('[TransfersPage] Delayed refresh after transfer creation');
        mutateTransfers(undefined, { revalidate: true });
      }, 1000);
      
      if (onTransferInitiated) onTransferInitiated(result);
    } catch (err) {
      console.error('Transfer failed:', err.response?.data || err.message);
      setShowConfirmation(false);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const sourceExchangeConfig = isTreasuryWallet(formData.sourceExchange) 
    ? null 
    : availableExchanges.find(e => e.id === formData.sourceExchange) || EXCHANGES.find(e => e.id === formData.sourceExchange);
  const destExchangeConfig = isTreasuryWallet(formData.destinationExchange)
    ? null
    : availableExchanges.find(e => e.id === formData.destinationExchange) || EXCHANGES.find(e => e.id === formData.destinationExchange);
  
  const sourceTreasuryWallet = isTreasuryWallet(formData.sourceExchange)
    ? treasuryWalletsList.find(w => w.address === getTreasuryWalletAddress(formData.sourceExchange))
    : null;
  const destTreasuryWallet = isTreasuryWallet(formData.destinationExchange)
    ? treasuryWalletsList.find(w => w.address === getTreasuryWalletAddress(formData.destinationExchange))
    : null;

  // For exchanges with dynamic deposit addresses (like Lighter), fetch from connector
  const isSourceTreasury = isTreasuryWallet(formData.sourceExchange);
  const targetChain = isSourceTreasury ? formData.sourceChain : (formData.chain || 'arbitrum');
  const shouldFetchFromConnector = formData.destinationExchange === 'lighter' && formData.destinationExchange && !isTreasuryWallet(formData.destinationExchange);
  
  const { data: connectorDepositAddress } = useSWR(
    shouldFetchFromConnector 
      ? `/balances/deposit-address?exchange=${formData.destinationExchange}&asset=${formData.asset || 'USDC'}&chain=${targetChain}`
      : null,
    swrFetcher,
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );

  const destDepositAddress = useMemo(() => {
    if (isTreasuryWallet(formData.destinationExchange)) return destTreasuryWallet?.address;
    if (!formData.destinationExchange) return null;
    
    // For Lighter, use connector address (bridge address)
    if (formData.destinationExchange === 'lighter' && connectorDepositAddress?.address) {
      return connectorDepositAddress.address;
    }
    
    // For other exchanges, use whitelist address
    if (!whitelistData?.exchanges) return null;
    const exchangeConfig = whitelistData.exchanges[formData.destinationExchange];
    const address = exchangeConfig?.depositAddresses?.[targetChain];
    
    // Return null if address is empty string (treat as not configured)
    return address && address.trim() ? address : null;
  }, [whitelistData, formData.destinationExchange, formData.chain, formData.sourceChain, formData.sourceExchange, destTreasuryWallet, connectorDepositAddress, targetChain]);

  const USDC_ONLY_DESTINATIONS = ['hyperliquid', 'extended', 'lighter'];
  const destIsUsdcOnly = USDC_ONLY_DESTINATIONS.includes(formData.destinationExchange);
  const destUsdcOnlyName = formData.destinationExchange === 'hyperliquid' ? 'Hyperliquid' : 
                           formData.destinationExchange === 'lighter' ? 'Lighter' : 'Extended';
  const showUsdcOnlySwapNote = destIsUsdcOnly;
  const usdcOnlySwapMessage = destIsUsdcOnly
    ? (formData.asset === 'USDT'
        ? `${destUsdcOnlyName} accepts USDC only. USDT will be auto-swapped to USDC on Arbitrum (Uniswap V3) before deposit.`
        : formData.destinationExchange === 'lighter'
        ? `${destUsdcOnlyName} accepts USDC deposits. Deposits are sent directly to the bridge contract address.`
        : `${destUsdcOnlyName} accepts USDC deposits on Arbitrum only.`)
    : '';

  return (
    <div className="transfers-page">
      {/* Mobile Tab Navigation */}
      <div className="mobile-tab-nav">
        <button 
          className={`mobile-tab ${mobileView === 'form' ? 'active' : ''}`}
          onClick={() => setMobileView('form')}
        >
          <ArrowLeftRight size={18} />
          <span>New Transfer</span>
        </button>
        <button 
          className={`mobile-tab ${mobileView === 'transfers' ? 'active' : ''}`}
          onClick={() => setMobileView('transfers')}
        >
          <History size={18} />
          <span>Transfers</span>
          {activeTransfers?.length > 0 && (
            <span className="mobile-tab-badge">{activeTransfers.length}</span>
          )}
        </button>
      </div>

      <div className="transfers-layout">
        {/* Left Column - New Transfer / Swap */}
        <div className={`transfer-form-panel ${mobileView !== 'form' ? 'mobile-hidden' : ''}`}>
          <div className="panel-header" style={{ 
            borderBottom: 'none', 
            paddingBottom: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
          }}>
            <button
              type="button"
              onClick={() => setActiveTab('transfer')}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'transfer' ? '2px solid #666' : '2px solid transparent',
                padding: '14px 0',
                fontSize: '0.95rem',
                fontWeight: activeTab === 'transfer' ? '500' : '400',
                color: activeTab === 'transfer' ? '#fff' : '#666',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeftRight size={16} />
                <span>Transfer</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('swap')}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === 'swap' ? '2px solid #666' : '2px solid transparent',
                padding: '14px 0',
                fontSize: '0.95rem',
                fontWeight: activeTab === 'swap' ? '500' : '400',
                color: activeTab === 'swap' ? '#fff' : '#666',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RefreshCw size={16} />
                <span>Swap</span>
              </div>
            </button>
          </div>
          
          <div style={{ borderTop: '1px solid #2a2a2a' }} />

          {/* Transfer Form */}
          {activeTab === 'transfer' && (
            <>
              {error && (
                <div className="error-banner">
                  {error}
                  <button onClick={clearError} className="dismiss-btn">×</button>
                </div>
              )}

              <form onSubmit={handlePreSubmit}>
            {/* From Section */}
            <div className="transfer-section">
              <div className="section-label">From</div>
              <div className="exchange-select-row">
                <select
                  name="sourceExchange"
                  value={formData.sourceExchange}
                  onChange={handleChange}
                  className={validationErrors.sourceExchange ? 'error' : ''}
                  disabled={isSubmitting}
                >
                  <option value="">Select exchange...</option>
                  {/* Wallets */}
                  {regularWalletsList.length > 0 && (
                    <optgroup label="Wallets">
                      {regularWalletsList
                        .filter(w => `treasury:${w.address}` !== formData.destinationExchange)
                        .map(wallet => (
                          <option key={wallet.address} value={`treasury:${wallet.address}`}>
                            {wallet.label} ({shortenAddress(wallet.address)})
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* Proxy Wallets */}
                  {intermediaryWalletsList.length > 0 && (
                    <optgroup label="Proxy Wallets">
                      {intermediaryWalletsList
                        .filter(w => `treasury:${w.address}` !== formData.destinationExchange)
                        .map(wallet => (
                          <option key={wallet.address} value={`treasury:${wallet.address}`}>
                            {wallet.label} ({shortenAddress(wallet.address)})
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* Exchanges */}
                  {availableExchanges.length > 0 && (
                    <optgroup label="Exchanges">
                      {availableExchanges
                        // Allow same exchange for internal transfers (will be validated by account types)
                        .map(ex => (
                          <option key={ex.id} value={ex.id}>{ex.name}</option>
                        ))}
                    </optgroup>
                  )}
                </select>
                {/* Account Type Selector - Show for exchanges that support account types */}
                {!isTreasuryWallet(formData.sourceExchange) && (() => {
                  const supportedTypes = EXCHANGE_ACCOUNT_TYPES[formData.sourceExchange] || [];
                  if (supportedTypes.length === 0) return null;
                  
                  const accountTypeLabels = {
                    spot: 'Spot',
                    perp: 'Perp',
                    futures: 'Futures',
                    margin: 'Margin',
                    funding: 'Funding',
                    main: 'Main',
                    trade: 'Trade',
                    unified: 'Unified',
                  };
                  
                  return (
                    <select
                      name="sourceAccountType"
                      value={formData.sourceAccountType}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="account-type-select"
                      title={formData.sourceExchange === formData.destinationExchange 
                        ? "Source account type for internal transfer" 
                        : "Account type to transfer from"}
                    >
                      <option value="">All accounts</option>
                      {supportedTypes.map(type => (
                        <option key={type} value={type}>
                          {accountTypeLabels[type] || type}
                        </option>
                      ))}
                    </select>
                  );
                })()}
                {sourceTreasuryWallet && (
                  <>
                    <div className="treasury-wallet-info">
                      <span className="treasury-wallet-label">{sourceTreasuryWallet.label}</span>
                      <span className="treasury-wallet-address mono">{shortenAddress(sourceTreasuryWallet.address)}</span>
                    </div>
                    <select
                      name="sourceChain"
                      value={formData.sourceChain}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="chain-select"
                    >
                      {sourceTreasuryWallet.chains.map(chain => (
                        <option key={chain} value={chain}>
                          {chain === 'arbitrum' ? 'Arbitrum' : chain === 'ethereum' ? 'Ethereum' : chain.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                {/* Reverse Button - Inline with exchange selector */}
                {(formData.sourceExchange || formData.destinationExchange) && (
                  <button
                    type="button"
                    className="reverse-btn-inline"
                    onClick={handleReverse}
                    disabled={isSubmitting}
                    title="Reverse source and destination"
                  >
                    <ArrowLeftRight size={16} />
                  </button>
                )}
              </div>

              {formData.sourceExchange && (
                <div className="balance-section">
                  <div className="balance-header">
                    <span className="balance-label">
                      Available Balance
                      {isTreasuryWallet(formData.sourceExchange) && sourceTreasuryWallet && (
                        <span className="balance-chain-badge">{formData.sourceChain.toUpperCase()}</span>
                      )}
                      {(fetchingBalanceFor === 'source' || isBalancesValidating) && (
                        <Loader2 className="balance-spinner" size={14} />
                      )}
                    </span>
                    {sourceBalances ? (
                      <span className="balance-main">{formatBalance(sourceBalances[formData.asset]?.available || 0)} {formData.asset}</span>
                    ) : (
                      <span className="balance-main loading">Loading...</span>
                    )}
                  </div>
                  {sourceBalances && (
                    <div className="balance-breakdown">
                      {ASSETS.map(asset => {
                        const bal = sourceBalances[asset];
                        if (!bal) return null;
                        return (
                          <div key={asset} className="balance-item">
                            <span className="asset-name">{asset}:</span>
                            <span className="asset-balance mono">{formatBalance(bal.available)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Treasury Route - Only show when source is exchange and destination is exchange */}
            {!isTreasuryWallet(formData.sourceExchange) && !isTreasuryWallet(formData.destinationExchange) && (
              <div className="treasury-route">
                <span className="route-arrow">↓</span>
                <div className="treasury-wallet-selector">
                  <div className="treasury-label-row">
                    <span className="route-label">via Proxy</span>
                    <span className="route-chain">{formData.chain.toUpperCase()}</span>
                  </div>
                  {selectedTreasuryWallet && (
                    <div className="treasury-address-row">
                      <div className="treasury-address-display">
                        <button
                          type="button"
                          onClick={() => copyToClipboard(selectedTreasuryWallet.address)}
                          className="btn-copy-address"
                          title="Copy address"
                        >
                          <Copy size={12} />
                        </button>
                        <span className="treasury-address mono" title={selectedTreasuryWallet.address}>
                          {shortenAddress(selectedTreasuryWallet.address)}
                        </span>
                        {selectedTreasuryWallet.name && (
                          <span className="treasury-wallet-name">{selectedTreasuryWallet.name}</span>
                        )}
                      </div>
                      {intermediaryWalletsList.length > 1 && (
                        <div className="treasury-selector-wrapper">
                          <button
                            type="button"
                            onClick={() => setShowTreasurySelector(!showTreasurySelector)}
                            className="btn-select-treasury"
                            disabled={isSubmitting}
                          >
                            <ChevronDown size={12} />
                          </button>
                          {showTreasurySelector && (
                            <div className="treasury-dropdown">
                              {intermediaryWalletsList.map((wallet) => (
                                <button
                                  key={wallet.address}
                                  type="button"
                                  className={`treasury-option ${wallet.address === formData.treasuryWallet ? 'selected' : ''} ${wallet.isMain ? 'is-main' : ''}`}
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, treasuryWallet: wallet.address }));
                                    setShowTreasurySelector(false);
                                  }}
                                >
                                  <div className="treasury-option-header">
                                    <div className="treasury-option-label">
                                      {wallet.label}
                                      {wallet.isMain && <span className="main-indicator">Main</span>}
                                    </div>
                                    <div className="treasury-option-chains">
                                      {wallet.chains.map(chain => (
                                        <span key={chain} className={`treasury-chain-tag ${chain}`}>
                                          {chain === 'arbitrum' ? 'ARB' : chain === 'ethereum' ? 'ETH' : chain.toUpperCase()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="treasury-option-address mono">{shortenAddress(wallet.address, 8, 6)}</div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <span className="route-arrow">↓</span>
              </div>
            )}
            
            {/* Direct Route - Show when source is treasury wallet */}
            {isTreasuryWallet(formData.sourceExchange) && formData.destinationExchange && (
              <div className="direct-route">
                <span className="route-arrow direct">↓</span>
                <div className="direct-route-info">
                  <span className="route-label">Direct Transfer</span>
                  {sourceTreasuryWallet && (
                    <span className="route-chain-badge">{formData.sourceChain.toUpperCase()}</span>
                  )}
                </div>
                <span className="route-arrow direct">↓</span>
              </div>
            )}

            {/* To Section */}
            <div className="transfer-section">
              <div className="section-label">To</div>
              <div className="exchange-select-row">
                <select
                  name="destinationExchange"
                  value={formData.destinationExchange}
                  onChange={handleChange}
                  className={validationErrors.destinationExchange ? 'error' : ''}
                  disabled={isSubmitting}
                >
                  <option value="">Select exchange...</option>
                  {/* Wallets */}
                  {regularWalletsList.length > 0 && (
                    <optgroup label="Wallets">
                      {regularWalletsList
                        .filter(w => `treasury:${w.address}` !== formData.sourceExchange)
                        .map(wallet => (
                          <option key={wallet.address} value={`treasury:${wallet.address}`}>
                            {wallet.label} ({shortenAddress(wallet.address)})
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* Proxy Wallets */}
                  {intermediaryWalletsList.length > 0 && (
                    <optgroup label="Proxy Wallets">
                      {intermediaryWalletsList
                        .filter(w => `treasury:${w.address}` !== formData.sourceExchange)
                        .map(wallet => (
                          <option key={wallet.address} value={`treasury:${wallet.address}`}>
                            {wallet.label} ({shortenAddress(wallet.address)})
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* Exchanges */}
                  {availableExchanges.length > 0 && (
                    <optgroup label="Exchanges">
                      {availableExchanges
                        // Allow same exchange for internal transfers (will be validated by account types)
                        .map(ex => (
                          <option key={ex.id} value={ex.id}>{ex.name}</option>
                        ))}
                    </optgroup>
                  )}
                </select>
                {/* Account Type Selector - Show for exchanges that support account types */}
                {!isTreasuryWallet(formData.destinationExchange) && (() => {
                  const supportedTypes = EXCHANGE_ACCOUNT_TYPES[formData.destinationExchange] || [];
                  if (supportedTypes.length === 0) return null;
                  
                  const accountTypeLabels = {
                    spot: 'Spot',
                    perp: 'Perp',
                    futures: 'Futures',
                    margin: 'Margin',
                    funding: 'Funding',
                    main: 'Main',
                    trade: 'Trade',
                    unified: 'Unified',
                  };
                  
                  return (
                    <select
                      name="destinationAccountType"
                      value={formData.destinationAccountType}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="account-type-select"
                      title={formData.sourceExchange === formData.destinationExchange 
                        ? "Destination account type for internal transfer" 
                        : "Account type to transfer to"}
                    >
                      <option value="">All accounts</option>
                      {supportedTypes.map(type => (
                        <option key={type} value={type}>
                          {accountTypeLabels[type] || type}
                        </option>
                      ))}
                    </select>
                  );
                })()}
                {destTreasuryWallet && (
                  <>
                    <div className="treasury-wallet-info">
                      <span className="treasury-wallet-label">{destTreasuryWallet.label}</span>
                      <span className="treasury-wallet-address mono">{shortenAddress(destTreasuryWallet.address)}</span>
                    </div>
                    <select
                      name="destinationChain"
                      value={formData.destinationChain}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className="chain-select"
                    >
                      {destTreasuryWallet.chains.map(chain => (
                        <option key={chain} value={chain}>
                          {chain === 'arbitrum' ? 'Arbitrum' : chain === 'ethereum' ? 'Ethereum' : chain.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              {formData.destinationExchange && (
                <div className="balance-section">
                  <div className="balance-header">
                    <span className="balance-label">
                      Current Balance
                      {isTreasuryWallet(formData.destinationExchange) && destTreasuryWallet && (
                        <span className="balance-chain-badge">{formData.destinationChain.toUpperCase()}</span>
                      )}
                      {(fetchingBalanceFor === 'destination' || isBalancesValidating) && (
                        <Loader2 className="balance-spinner" size={14} />
                      )}
                    </span>
                    {destBalances ? (
                      <span className="balance-main">${
                        // For Hyperliquid, always show USDC balance (it only supports USDC)
                        formData.destinationExchange === 'hyperliquid' 
                          ? formatBalance(destBalances['USDC']?.available || 0)
                          : formatBalance(destBalances[formData.asset]?.available || 0)
                      }</span>
                    ) : (
                      <span className="balance-main loading">Loading...</span>
                    )}
                  </div>
                  {destBalances && (
                    <div className="balance-breakdown">
                      {ASSETS.map(asset => {
                        const bal = destBalances[asset];
                        if (!bal) return null;
                        return (
                          <div key={asset} className="balance-item">
                            <span className="asset-name">{asset}:</span>
                            <span className="asset-balance mono">{formatBalance(bal.available)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Asset and Amount */}
            <div className="transfer-section">
              <div className="asset-amount-row">
                <div className="form-field asset-field">
                  <label>Asset</label>
                  <select
                    name="asset"
                    value={formData.asset}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  >
                    {ASSETS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  {(formData.transferType === 'exchange-to-exchange' || formData.transferType === 'exchange-to-treasury') && formData.sourceExchange && (
                    <button type="button" className="action-btn max-btn-below" onClick={handleSetMax} disabled={isSubmitting || !sourceBalances?.[formData.asset]}>
                      <span className="btn-icon">⚡</span>
                      <span>
                        {sourceBalances?.[formData.asset] 
                          ? `Max (${formatBalance(sourceBalances[formData.asset].available)})`
                          : 'Max'
                        }
                      </span>
                    </button>
                  )}
                </div>

                <div className="form-field amount-field">
                  <label>Amount</label>
                  <div className="amount-input-wrapper">
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className={validationErrors.amount ? 'error' : ''}
                      disabled={isSubmitting}
                    />
                    <span className="amount-unit">{formData.asset}</span>
                  </div>
                  {validationErrors.amount && <span className="field-error">{validationErrors.amount}</span>}
                  
                  {formData.transferType === 'exchange-to-exchange' && formData.sourceExchange && formData.destinationExchange && (
                    <button 
                      type="button" 
                      className="action-btn equalize-btn-below" 
                      onClick={handleEqualize} 
                      disabled={isSubmitting || !equalizeAmount || !sourceBalances?.[formData.asset] || !destBalances?.[formData.asset]}
                    >
                      <span className="btn-icon">☰</span>
                      <span>
                        {equalizeAmount 
                          ? `Equalize (${formatBalance(equalizeAmount)})`
                          : 'Equalize'
                        }
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {showUsdcOnlySwapNote && (
                <div className="info-note">
                  <Info size={14} />
                  <span>{usdcOnlySwapMessage}</span>
                </div>
              )}

              {equalizeAmount && formData.sourceExchange && formData.destinationExchange && (
                <div className="equalize-suggestion-box">
                  <div className="equalize-header">
                    <span className="equalize-icon">☰</span>
                    <span>Equalize Balance</span>
                  </div>
                  <div className="equalize-text">
                    Transfer <strong>{formatBalance(equalizeAmount)} {formData.asset}</strong> to balance both exchanges at <strong>~{formatBalance((parseFloat(sourceBalances[formData.asset]?.available || 0) + parseFloat(destBalances[formData.asset]?.available || 0)) / 2)}</strong> each
                  </div>
                </div>
              )}
            </div>

            {/* Network Selection */}
            <div className="transfer-section">
              <div className="section-label">Network</div>
              <div className="network-cards">
                {CHAINS.map(chain => (
                  <button
                    key={chain.id}
                    type="button"
                    className={`network-card ${formData.chain === chain.id ? 'active' : ''}`}
                    onClick={() => handleChange({ target: { name: 'chain', value: chain.id } })}
                    disabled={isSubmitting}
                  >
                    <div className="network-name">{chain.name}</div>
                    <div className="network-details">
                      <span>{chain.cost}</span>
                      <span className="separator">•</span>
                      <span>{chain.time}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                <RotateCcw size={16} />
                <span>Cancel</span>
              </button>
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Processing...' : 'Create Transfer'}
              </button>
            </div>
          </form>
          </>
          )}

          {/* Swap Form */}
          {activeTab === 'swap' && (
            <SwapForm />
          )}
        </div>

        {/* Right Column - Active & Recent Transfers */}
        <div className={`transfers-status-panel ${mobileView !== 'transfers' ? 'mobile-hidden' : ''}`}>
          {/* Active Transfers */}
          <div className="status-section">
            <div className="section-header">
              <h4>Active Transfers</h4>
              {activeTransfers?.length > 0 && (
                <span className="count-badge">{activeTransfers.length}</span>
              )}
            </div>
            <div className="transfers-list">
              {activeTransfers && activeTransfers.length > 0 ? (
                activeTransfers.map(transfer => {
                  // API returns nested source/destination objects
                  const sourceExchange = transfer.source?.exchange || transfer.source_exchange || 'Unknown';
                  const destExchange = transfer.destination?.exchange || transfer.destination_exchange || 'Unknown';
                  const amount = transfer.source?.amount || transfer.amount || '0';
                  const asset = transfer.source?.asset || transfer.asset || 'USDT';
                  const chain = transfer.source?.chain || transfer.chain || 'arbitrum';
                  const createdAt = transfer.createdAt || transfer.created_at;
                  const errorMessage = transfer.errorMessage || transfer.error_message;
                  
                  // Get addresses from steps if available
                  const steps = transfer.steps || [];
                  const firstStep = steps[0];
                  const lastStep = steps[steps.length - 1];
                  const fromAddress = firstStep?.fromAddress || (sourceExchange.startsWith('0x') ? sourceExchange : null);
                  const toAddress = lastStep?.toAddress || (destExchange.startsWith('0x') ? destExchange : null);
                  
                  // Check if source/dest is a wallet address
                  const sourceIsWallet = sourceExchange?.startsWith('0x');
                  const destIsWallet = destExchange?.startsWith('0x');
                  
                  // Get wallet info from treasuryWalletsList
                  const getWalletInfo = (address) => {
                    if (!address?.startsWith('0x')) return null;
                    return treasuryWalletsList.find(w => w.address.toLowerCase() === address.toLowerCase());
                  };
                  
                  const sourceWalletInfo = getWalletInfo(sourceExchange);
                  const destWalletInfo = getWalletInfo(destExchange);
                  
                  // Format display name with wallet name if available
                  const formatName = (endpoint, walletInfo) => {
                    if (!endpoint) return 'Unknown';
                    if (endpoint.startsWith('0x')) {
                      return walletInfo?.label || walletInfo?.name || 'Wallet';
                    }
                    return endpoint.charAt(0).toUpperCase() + endpoint.slice(1);
                  };
                  
                  const srcAddr = sourceIsWallet ? sourceExchange : fromAddress;
                  const dstAddr = destIsWallet ? destExchange : toAddress;
                  
                  const initiatedBy = transfer.initiatedBy || transfer.initiated_by || 'api';
                  
                  // Debug: Log cancellation window data (throttled to avoid spam)
                  if (transfer.cancellationWindowStartedAt && Math.random() < 0.01) { // Only log 1% of renders
                    console.log('[DEBUG] Cancellation window data:', {
                      transferId: transfer.id,
                      cancellationWindowStartedAt: transfer.cancellationWindowStartedAt,
                      status: transfer.status,
                      cancellationWindowDuration,
                    });
                  }
                  
                  
                  const handleToggleInitiatedBy = async (e) => {
                    e.stopPropagation();
                    const newValue = initiatedBy === 'gui' ? 'api' : 'gui';
                    try {
                      await updateTransferInitiatedBy(transfer.id, newValue);
                      mutateTransfers();
                    } catch (err) {
                      console.error('Failed to update initiated_by:', err);
                    }
                  };
                  
                  return (
                    <div key={transfer.id} className={`transfer-item compact ${transfer.status?.toLowerCase()}`}>
                      <div className="transfer-header-row">
                        <div className="transfer-tags">
                          <span className={`tag source-tag ${initiatedBy}`} onClick={handleToggleInitiatedBy} style={{ cursor: 'pointer', userSelect: 'none' }} title="Click to toggle GUI/API">{initiatedBy === 'gui' ? 'GUI' : 'API'}</span>
                          <span className={`tag status ${transfer.status?.toLowerCase()}`}>
                            {transfer.status?.replace('_', ' ')}
                          </span>
                          <span className="network-tag small">{chain?.toUpperCase()}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button 
                            className="btn-icon-tiny" 
                            onClick={(e) => {
                              e.stopPropagation();
                              populateFormFromTransfer(transfer);
                            }}
                            title="Copy parameters to form"
                            style={{ marginLeft: 'auto' }}
                          >
                            <Copy size={12} />
                          </button>
                          <span className="transfer-time-small">{createdAt ? formatTimestampWithTimezone(createdAt, { timezone: activeTimezone, includeDate: false, includeTime: true, format: 'time' }) : ''}</span>
                        </div>
                      </div>
                      
                      <div className="transfer-route-single-line">
                        <span className="endpoint-name">{formatName(sourceExchange, sourceWalletInfo)}</span>
                        {sourceWalletInfo?.isMain && <span className="main-tag">Main</span>}
                        {srcAddr && (
                          <div className="addr-copy">
                            <span className="mono">{shortenAddress(srcAddr, 4, 4)}</span>
                            <button className="btn-icon-tiny" onClick={(e) => { e.stopPropagation(); copyToClipboard(srcAddr); }}><Copy size={10} /></button>
                          </div>
                        )}
                        <span className="route-arrow-small">→</span>
                        <span className="endpoint-name">{formatName(destExchange, destWalletInfo)}</span>
                        {destWalletInfo?.isMain && <span className="main-tag">Main</span>}
                        {dstAddr && (
                          <div className="addr-copy">
                            <span className="mono">{shortenAddress(dstAddr, 4, 4)}</span>
                            <button className="btn-icon-tiny" onClick={(e) => { e.stopPropagation(); copyToClipboard(dstAddr); }}><Copy size={10} /></button>
                          </div>
                        )}
                        <span className="amount-inline">{parseFloat(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} {asset}</span>
                      </div>
                      
                      {errorMessage && (
                        <div className={`transfer-error-small ${errorMessage.toLowerCase().includes('cancelled') ? 'cancelled' : ''}`}>
                          {errorMessage}
                        </div>
                      )}
                      
                      {/* Cancellation Window Display - Reuse treasury countdown style */}
                      {/* Only show cancellation window if NOT in treasury (treasury timer takes priority) */}
                      {/* Only show for active transfers, never for completed/failed/cancelled */}
                      {/* CRITICAL: Also check if transfer has completed_at to prevent showing timer on completed transfers */}
                      {transfer.status?.toLowerCase() !== 'in_treasury' && 
                       !['completed', 'failed', 'cancelled', 'rejected'].includes(transfer.status?.toLowerCase() || '') &&
                       !transfer.completedAt && !transfer.completed_at && (() => {
                        // Show cancellation window for active transfers that could have cancellation
                        const transferStatus = transfer.status?.toLowerCase() || '';
                        const isActiveTransfer = ['pending', 'validated', 'withdrawing', 'swapping', 'depositing', 'in_progress'].includes(transferStatus);
                        
                        
                        if (!isActiveTransfer || cancellationWindowDuration <= 0) {
                          return null;
                        }
                        
                        // Use backend-provided timer if available (authoritative), otherwise fallback to client calculation
                        const timer = transfer.timers?.cancellationWindow;
                        let remaining = 0;
                        let isActive = false;
                        let isExpired = false;
                        const hasActiveWindow = !!transfer.cancellationWindowStartedAt; // Define before if/else for debug log
                        
                        if (timer) {
                          // Use backend-provided timer values (authoritative, server time)
                          // Ensure remaining is never negative (safety check)
                          remaining = Math.max(0, timer.remainingSeconds);
                          isActive = timer.isActive && remaining > 0;
                          isExpired = timer.isExpired || remaining <= 0;
                        } else {
                          // Fallback: client-side calculation (for backward compatibility)
                          if (hasActiveWindow) {
                            const windowStart = new Date(transfer.cancellationWindowStartedAt);
                            const now = new Date();
                            const elapsed = Math.floor((now - windowStart) / 1000);
                            remaining = Math.max(0, cancellationWindowDuration - elapsed);
                            isActive = remaining > 0;
                            isExpired = elapsed >= cancellationWindowDuration;
                          } else {
                            return null;
                          }
                        }
                        
                        // Force re-render when countdownTick changes (for dynamic updates)
                        const _ = countdownTick; // eslint-disable-line no-unused-vars
                        
                        
                        // Ensure remaining is never negative (safety check)
                        remaining = Math.max(0, remaining);
                        
                        // Show cancellation window only if active and remaining > 0
                        // Don't show for completed/failed/cancelled transfers or expired windows
                        if (!isActive || remaining <= 0 || isExpired) {
                          // Window expired or transfer completed, don't show
                          return null;
                        }
                        
                        return (
                          <div className={`treasury-countdown cancellation-window ${isActive && remaining > 0 ? 'active' : 'expired'}`}>
                            {isActive && remaining > 0 ? (
                              <div className="countdown-timer">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                  <Clock size={10} className="countdown-clock" />
                                  <span className="countdown-text">Withdrawal will be sent in</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <span className="countdown-value">{remaining}s</span>
                                  <button
                                    className="btn-cancel-transfer"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await cancelTransfer(transfer.id);
                                        mutateTransfers();
                                      } catch (err) {
                                        alert('Failed to cancel transfer: ' + (err.response?.data?.error || err.message));
                                      }
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                                <div className="countdown-bar">
                                  <div 
                                    className="countdown-progress" 
                                    style={{ width: `${((cancellationWindowDuration - remaining) / cancellationWindowDuration) * 100}%` }}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                      
                      {/* Deposit Confirmation Timer - for depositing status transfers */}
                      {/* CRITICAL: Also check if transfer has completed_at to prevent showing timer on completed transfers */}
                      {/* CRITICAL: Do NOT show deposit confirmation if cancellation window is active (cancellation window takes priority) */}
                      {transfer.status?.toLowerCase() === 'depositing' && 
                       !transfer.completedAt && !transfer.completed_at && (() => {
                        // Force re-render when countdownTick changes (for dynamic updates)
                        const _ = countdownTick; // eslint-disable-line no-unused-vars
                        
                        // Check if cancellation window is active (same logic as cancellation window display above)
                        const cancellationTimer = transfer.timers?.cancellationWindow;
                        const hasActiveCancellationWindow = cancellationTimer
                          ? (cancellationTimer.isActive && cancellationTimer.remainingSeconds > 0)
                          : (transfer.cancellationWindowStartedAt && (() => {
                              const windowStart = new Date(transfer.cancellationWindowStartedAt);
                              const now = new Date();
                              const elapsed = Math.floor((now - windowStart) / 1000);
                              const remaining = Math.max(0, cancellationWindowDuration - elapsed);
                              return remaining > 0;
                            })());
                        
                        
                        // If cancellation window is active, don't show deposit confirmation
                        if (hasActiveCancellationWindow) {
                          return null;
                        }
                        
                        const depositTimer = transfer.timers?.depositConfirmation;
                        
                        // Calculate elapsed time client-side for real-time updates
                        let remaining = 0;
                        let elapsed = 0;
                        let isActive = false;
                        
                        if (depositTimer && depositTimer.startedAt) {
                          const startedAt = new Date(depositTimer.startedAt);
                          const now = new Date();
                          const elapsedMs = now.getTime() - startedAt.getTime();
                          elapsed = Math.floor(elapsedMs / 1000);
                          const maxDuration = depositTimer.maxDurationSeconds;
                          remaining = Math.max(0, maxDuration - elapsed);
                          isActive = remaining > 0 && elapsed < maxDuration;
                        } else {
                          // Fallback: use backend values if available
                          if (depositTimer) {
                            // Ensure remaining is never negative (safety check)
                            remaining = Math.max(0, depositTimer.remainingSeconds || 0);
                            elapsed = depositTimer.elapsedSeconds || 0;
                            isActive = depositTimer.isActive && remaining > 0;
                          }
                        }
                        
                        // Final safety check: ensure remaining is never negative
                        remaining = Math.max(0, remaining);
                        
                        if (!depositTimer || !isActive) {
                          // Show status indicator if timer expired or not available
                          return (
                            <div className="transfer-status-indicator">
                              <Clock size={12} className="countdown-clock" />
                              <span className="countdown-text">Waiting for deposit confirmation...</span>
                            </div>
                          );
                        }

                        const maxDuration = depositTimer.maxDurationSeconds;
                        
                        return (
                          <div className="treasury-countdown deposit-confirmation">
                            <div className="countdown-timer">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                <Clock size={10} className="countdown-clock" />
                                <span className="countdown-text">Deposit confirmation: {remaining}s remaining</span>
                              </div>
                              <div className="countdown-bar">
                                <div 
                                  className="countdown-progress" 
                                  style={{ width: `${Math.min(100, (elapsed / maxDuration) * 100)}%` }}
                                />
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                                Elapsed: {Math.floor(elapsed / 60)}m {elapsed % 60}s / Max: {Math.floor(maxDuration / 60)}m
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Treasury Countdown Display - Simplified UI */}
                      {/* CRITICAL: Only show treasury hold timer for in_treasury status transfers */}
                      {/* Do NOT show for depositing, completed, or other statuses */}
                      {(transfer.status?.toLowerCase() === 'in_treasury') && (() => {
                        // #region agent log
                        fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:1874',message:'Rendering treasury countdown check',data:{transferId:transfer.id,status:transfer.status,hasTreasuryReceivedAt:!!(transfer.treasuryReceivedAt||transfer.treasury_received_at),hasTimers:!!transfer.timers,hasTreasuryHold:!!transfer.timers?.treasuryHold,hasTreasuryCountdown:!!transfer.treasuryCountdown},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
                        // #endregion
                        
                        // STALE CHECK: Don't show countdown for very old in_treasury transfers
                        // (they are likely stuck and should show a different message)
                        const receivedAtTime = new Date(transfer.treasuryReceivedAt || transfer.treasury_received_at).getTime();
                        // Use backend-provided holdTimeSeconds (prefer new format, fallback to legacy)
                        // CRITICAL: Don't use hardcoded 60-second fallback - if backend doesn't provide it,
                        // calculate from elapsed time to avoid showing wrong duration
                        let holdTimeSeconds = transfer.timers?.treasuryHold?.holdTimeSeconds 
                          || transfer.treasuryCountdown?.holdTimeSeconds;
                        
                        // If holdTimeSeconds is not provided, try to infer from remainingSeconds and elapsed time
                        if (!holdTimeSeconds || holdTimeSeconds === 60) {
                          const remainingSeconds = transfer.timers?.treasuryHold?.remainingSeconds 
                            || transfer.treasuryCountdown?.remainingSeconds;
                          if (remainingSeconds !== undefined && remainingSeconds !== null) {
                            const elapsed = Math.floor((Date.now() - receivedAtTime) / 1000);
                            const inferredHoldTime = remainingSeconds + elapsed;
                            // Only use inferred value if it's reasonable (between 1 and 300 seconds)
                            // and different from the 60-second default
                            if (inferredHoldTime >= 1 && inferredHoldTime <= 300 && inferredHoldTime !== 60) {
                              holdTimeSeconds = inferredHoldTime;
                              console.log('[TransfersPage] Inferred holdTimeSeconds from remainingSeconds:', {
                                transferId: transfer.id,
                                remainingSeconds,
                                elapsed,
                                inferredHoldTimeSeconds: holdTimeSeconds,
                              });
                            } else if (holdTimeSeconds === 60) {
                              // If we have 60 seconds (default), log a warning but keep it
                              console.warn('[TransfersPage] Using 60s default - backend may not have provided correct value', {
                                transferId: transfer.id,
                                remainingSeconds,
                                elapsed,
                                inferredHoldTime,
                                hasTimers: !!transfer.timers,
                                hasTreasuryHold: !!transfer.timers?.treasuryHold,
                              });
                            }
                          } else if (!holdTimeSeconds) {
                            // Last resort: use 60 seconds but log a warning
                            console.warn('[TransfersPage] No holdTimeSeconds provided by backend, using 60s fallback', {
                              transferId: transfer.id,
                              hasTimers: !!transfer.timers,
                              hasTreasuryHold: !!transfer.timers?.treasuryHold,
                              hasTreasuryCountdown: !!transfer.treasuryCountdown,
                            });
                            holdTimeSeconds = 60;
                          }
                        }
                        const expectedCompleteTime = receivedAtTime + (holdTimeSeconds * 1000);
                        const staleThresholdMs = 5 * 60 * 1000; // 5 minutes past expected completion
                        const isStale = Date.now() > (expectedCompleteTime + staleThresholdMs);
                        
                        // For stale transfers, show "Sending to destination..." instead of countdown
                        if (isStale) {
                          return (
                            <div className="treasury-countdown">
                              <div className="countdown-timer">
                                <span className="countdown-text sending">Sending to destination...</span>
                              </div>
                            </div>
                          );
                        }
                        
                        // Use backend-provided timer if available (authoritative), otherwise fallback
                        // CRITICAL: Only show treasury hold timer for in_treasury status, NOT depositing
                        // For depositing transfers, the treasury hold has already completed
                        if (transfer.status?.toLowerCase() !== 'in_treasury') {
                          return null;
                        }
                        
                        const treasuryTimer = transfer.timers?.treasuryHold;
                        
                        // Log for debugging
                        console.log('[TransfersPage] Treasury timer data:', {
                          transferId: transfer.id,
                          hasTreasuryTimer: !!treasuryTimer,
                          holdTimeSeconds: treasuryTimer?.holdTimeSeconds || transfer.treasuryCountdown?.holdTimeSeconds || holdTimeSeconds,
                          remainingSeconds: treasuryTimer?.remainingSeconds || transfer.treasuryCountdown?.remainingSeconds,
                          receivedAt: transfer.treasuryReceivedAt || transfer.treasury_received_at,
                        });
                        
                        // Use backend remainingSeconds directly - it's already calculated with server time
                        // Don't try to adjust for elapsed time client-side as it causes timezone issues
                        let seconds = treasuryTimer?.remainingSeconds !== undefined
                          ? Math.max(0, treasuryTimer.remainingSeconds)
                          : getCountdownSeconds(transfer);
                        
                        // ROBUSTNESS: If seconds is null but we have treasuryReceivedAt, try to calculate it
                        if (seconds === null && (transfer.treasuryReceivedAt || transfer.treasury_received_at)) {
                          const receivedAt = new Date(transfer.treasuryReceivedAt || transfer.treasury_received_at);
                          const now = new Date();
                          const elapsed = Math.floor((now - receivedAt) / 1000);
                          const calculatedSeconds = Math.max(0, holdTimeSeconds - elapsed);
                          if (calculatedSeconds > 0) {
                            seconds = calculatedSeconds;
                            console.log('[TransfersPage] Calculated seconds from elapsed time:', {
                              transferId: transfer.id,
                              elapsed,
                              holdTimeSeconds,
                              calculatedSeconds,
                            });
                          }
                        }
                        // #region agent log
                        fetch('http://127.0.0.1:7242/ingest/1e092eb0-bcbe-43ec-a480-4f999bf2f798',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TransfersPage.jsx:1978',message:'Countdown seconds calculated',data:{transferId:transfer.id,seconds,holdTimeSeconds,hasTreasuryTimer:!!treasuryTimer,remainingSeconds:treasuryTimer?.remainingSeconds,willRender:seconds!==null&&seconds>0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
                        // #endregion
                        
                        if (seconds === null || seconds <= 0) return null;
                        
                        // Check if cancellation window is still active (use backend timer if available)
                        const cancellationTimer = transfer.timers?.cancellationWindow;
                        const hasActiveCancellationWindow = cancellationTimer
                          ? cancellationTimer.isActive
                          : (transfer.cancellationWindowStartedAt && (() => {
                              const windowStart = new Date(transfer.cancellationWindowStartedAt);
                              const now = new Date();
                              const elapsed = Math.floor((now - windowStart) / 1000);
                              return Math.max(0, cancellationWindowDuration - elapsed) > 0;
                            })());
                        return (
                          <div className="treasury-countdown">
                            <div className="countdown-timer">
                              {seconds > 0 ? (
                                <>
                                  <Clock size={12} className="countdown-clock" />
                                  <span className="countdown-text">Sending to destination in</span>
                                  <span className="countdown-value">{seconds}s</span>
                                  <div className="countdown-bar">
                                    <div 
                                      className="countdown-progress" 
                                      style={{ 
                                        width: (() => {
                                          // Use backend timer holdTimeSeconds if available
                                          if (transfer.timers?.treasuryHold?.holdTimeSeconds) {
                                            return `${((transfer.timers.treasuryHold.holdTimeSeconds - seconds) / transfer.timers.treasuryHold.holdTimeSeconds) * 100}%`;
                                          }
                                          // Fallback to legacy treasuryCountdown
                                          if (transfer.treasuryCountdown?.holdTimeSeconds) {
                                            return `${((transfer.treasuryCountdown.holdTimeSeconds - seconds) / transfer.treasuryCountdown.holdTimeSeconds) * 100}%`;
                                          }
                                          // Use the holdTimeSeconds we calculated above (should never reach here if backend provides it)
                                          return `${((holdTimeSeconds - seconds) / holdTimeSeconds) * 100}%`;
                                        })()
                                      }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <span className="countdown-text sending">Sending to destination...</span>
                              )}
                            </div>
                            {/* Add cancel button to treasury countdown - only show if cancellation window is still active */}
                            {hasActiveCancellationWindow && (
                              <button
                                className="btn-cancel-transfer"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await cancelTransfer(transfer.id);
                                    mutateTransfers();
                                  } catch (err) {
                                    alert('Failed to cancel transfer: ' + (err.response?.data?.error || err.message));
                                  }
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      
                      <div className="transfer-footer-row">
                        <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                          {['failed', 'rejected'].includes(transfer.status?.toLowerCase()) && (
                            <button className="action-btn-tiny">Retry</button>
                          )}
                          {['in_progress', 'withdrawing', 'in_treasury', 'swapping', 'depositing', 'validated'].includes(transfer.status?.toLowerCase()) && (
                            <>
                              {((sourceExchange?.toLowerCase() === 'hyperliquid' && transfer.status?.toLowerCase() === 'withdrawing') ||
                                (destExchange?.toLowerCase() === 'hyperliquid' && transfer.status?.toLowerCase() === 'depositing')) && (
                                <button 
                                  className="action-btn-tiny" 
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const result = await recheckTransfer(transfer.id);
                                      if (result.updated) {
                                        alert('Transfer status updated! ' + (result.message || 'The transfer has been marked as completed.'));
                                        mutateTransfers();
                                      } else {
                                        alert(result.message || `Transfer still ${result.status}. The operation may still be processing (Hyperliquid takes 3-4 minutes).`);
                                      }
                                    } catch (error) {
                                      console.error('Failed to re-check transfer:', error);
                                      alert('Failed to re-check transfer: ' + (error.response?.data?.error || error.message));
                                    }
                                  }}
                                  title="Re-check status via Hyperliquid ledger API"
                                >
                                  Re-check
                                </button>
                              )}
                              <button 
                                className="action-btn-tiny" 
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Mark this transfer as completed? This should only be used if the transfer is actually completed on-chain but stuck in the system.')) {
                                    try {
                                      await markTransferComplete(transfer.id);
                                      mutateTransfers();
                                    } catch (err) {
                                      alert('Failed to mark transfer as complete: ' + (err.response?.data?.error || err.message));
                                    }
                                  }
                                }}
                              >
                                Mark Complete
                              </button>
                              <button 
                                className="action-btn-tiny" 
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Are you sure you want to cancel this transfer? This will mark it as failed and stop all processing.`)) {
                                    try {
                                      await cancelTransfer(transfer.id);
                                      mutateTransfers();
                                    } catch (err) {
                                      alert('Failed to cancel transfer: ' + (err.response?.data?.error || err.message));
                                    }
                                  }
                                }}
                              >
                                Stop
                              </button>
                            </>
                          )}
                        </div>
                        {/* Show "transfer order sent" on the right when cancellation window has expired */}
                        {(() => {
                          // Force re-render when countdownTick changes (for dynamic updates)
                          const _ = countdownTick; // eslint-disable-line no-unused-vars
                          
                          const transferStatus = transfer.status?.toLowerCase() || '';
                          const isActiveTransfer = ['pending', 'validated', 'withdrawing', 'swapping', 'depositing', 'in_progress'].includes(transferStatus);
                          if (!isActiveTransfer || cancellationWindowDuration <= 0) return null;
                          
                          const hasActiveWindow = transfer.cancellationWindowStartedAt;
                          if (!hasActiveWindow) return null;
                          
                          const windowStart = new Date(transfer.cancellationWindowStartedAt);
                          const now = new Date();
                          const elapsed = Math.floor((now - windowStart) / 1000);
                          const isExpired = elapsed >= cancellationWindowDuration;
                          
                          if (isExpired) {
                            return (
                              <span className="transfer-order-sent-text">transfer order sent</span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">No active transfers</div>
              )}
            </div>
          </div>

          {/* Recent Transfers */}
          <div className="status-section">
            <div className="section-header">
              <h4>Recent Transfers</h4>
              <a href="#" className="view-all-link" onClick={(e) => { e.preventDefault(); onNavigateToHistory && onNavigateToHistory(); }}>View all →</a>
            </div>
            <div className="transfers-list">
              {recentTransfers && recentTransfers.length > 0 ? (
                recentTransfers.map(transfer => {
                  // API returns nested source/destination objects
                  const sourceExchange = transfer.source?.exchange || transfer.source_exchange || 'Unknown';
                  const destExchange = transfer.destination?.exchange || transfer.destination_exchange || 'Unknown';
                  const amount = transfer.source?.amount || transfer.amount || '0';
                  const asset = transfer.source?.asset || transfer.asset || 'USDT';
                  const chain = transfer.source?.chain || transfer.chain || 'arbitrum';
                  const createdAt = transfer.createdAt || transfer.created_at;
                  const completedAt = transfer.completedAt || transfer.completed_at;
                  
                  // Get addresses from steps if available
                  const steps = transfer.steps || [];
                  const firstStep = steps[0];
                  const lastStep = steps[steps.length - 1];
                  const fromAddress = firstStep?.fromAddress || (sourceExchange?.startsWith('0x') ? sourceExchange : null);
                  const toAddress = lastStep?.toAddress || (destExchange?.startsWith('0x') ? destExchange : null);
                  
                  // Check if source/dest is a wallet address
                  const sourceIsWallet = sourceExchange?.startsWith('0x');
                  const destIsWallet = destExchange?.startsWith('0x');
                  
                  // Get wallet info from treasuryWalletsList
                  const getWalletInfo = (address) => {
                    if (!address?.startsWith('0x')) return null;
                    return treasuryWalletsList.find(w => w.address.toLowerCase() === address.toLowerCase());
                  };
                  
                  const sourceWalletInfo = getWalletInfo(sourceExchange);
                  const destWalletInfo = getWalletInfo(destExchange);
                  
                  // Format display name with wallet name if available
                  const formatName = (endpoint, walletInfo) => {
                    if (!endpoint) return 'Unknown';
                    if (endpoint.startsWith('0x')) {
                      return walletInfo?.label || walletInfo?.name || 'Wallet';
                    }
                    return endpoint.charAt(0).toUpperCase() + endpoint.slice(1);
                  };
                  
                  const srcAddr = sourceIsWallet ? sourceExchange : fromAddress;
                  const dstAddr = destIsWallet ? destExchange : toAddress;
                  
                  // Calculate duration (only show positive durations)
                  let duration = null;
                  if (createdAt && completedAt) {
                    const durationMs = new Date(completedAt) - new Date(createdAt);
                    // Only show positive durations (negative can happen with data inconsistency)
                    if (durationMs > 0 && durationMs < 60000) {
                      duration = `${Math.round(durationMs / 1000)}s`;
                    } else if (durationMs >= 60000) {
                      duration = `${Math.round(durationMs / 60000)}m`;
                    }
                    // If durationMs <= 0, duration stays null (won't be shown)
                  }
                  
                  // Calculate received amount from steps (last step amount minus fees)
                  // IMPORTANT: Never show received amount for failed/cancelled/rejected transfers
                  const initiatedBy = transfer.initiatedBy || transfer.initiated_by || 'api';
                  const status = transfer.status?.toLowerCase();
                  const isFailed = ['failed', 'cancelled', 'rejected'].includes(status);
                  const isCompleted = status === 'completed';
                  const errorMessage = transfer.errorMessage || transfer.error_message;
                  
                  // Determine destination asset (may differ from source asset if swap occurred)
                  // Check for swap step or final deposit step to get the actual received asset
                  let destinationAsset = asset; // Default to source asset
                  
                  // Special case: Hyperliquid only accepts USDC deposits
                  if (destExchange?.toLowerCase() === 'hyperliquid') {
                    destinationAsset = 'USDC';
                  } else {
                    const swapStep = steps.find(s => s.action === 'dex_swap');
                    if (swapStep && swapStep.destinationAsset) {
                      destinationAsset = swapStep.destinationAsset;
                    } else {
                      // Check final deposit step or onchain_transfer step for destination asset
                      const depositStep = steps.find(s => s.action === 'cex_deposit' || s.action === 'onchain_transfer');
                      if (depositStep && depositStep.asset) {
                        destinationAsset = depositStep.asset;
                      }
                    }
                  }
                  
                  // Only calculate received amount for completed transfers
                  // Explicitly set to null for failed transfers (ignore any API value)
                  let receivedAmount = null;
                  if (!isFailed && isCompleted) {
                    // Try to get the actual received amount from the deposit step first
                    const depositStep = steps.find(s => s.action === 'cex_deposit' || s.action === 'onchain_transfer');
                    if (depositStep && depositStep.amount) {
                      // Use the amount from the deposit step (this is what was actually received)
                      receivedAmount = parseFloat(depositStep.amount);
                    } else {
                      // Fallback: calculate from source amount minus fees
                      const totalFees = steps.reduce((sum, s) => sum + parseFloat(s.fee || 0), 0);
                      receivedAmount = parseFloat(amount) - totalFees;
                    }
                  }
                  // Ensure receivedAmount is null for failed transfers (even if API returns a value)
                  if (isFailed) {
                    receivedAmount = null;
                  }
                  
                  const handleToggleInitiatedBy = async (e) => {
                    e.stopPropagation();
                    const newValue = initiatedBy === 'gui' ? 'api' : 'gui';
                    try {
                      await updateTransferInitiatedBy(transfer.id, newValue);
                      mutateTransfers();
                    } catch (err) {
                      console.error('Failed to update initiated_by:', err);
                    }
                  };
                  
                  const handleCopyToForm = (e) => {
                    e.stopPropagation();
                    populateFormFromTransfer(transfer);
                  };
                  
                  return (
                    <div 
                      key={transfer.id} 
                      className={`transfer-item compact ${isFailed ? 'failed' : 'completed'}`}
                    >
                      <div className="transfer-header-row">
                        <div className="transfer-tags">
                          <span className={`tag source-tag small ${initiatedBy}`} onClick={handleToggleInitiatedBy} style={{ cursor: 'pointer', userSelect: 'none' }} title="Click to toggle GUI/API">{initiatedBy === 'gui' ? 'GUI' : 'API'}</span>
                          {isFailed ? (
                            <span className="tag failed-tag small" title={errorMessage || 'Transfer failed'}>
                              ✗
                              {errorMessage && <Info size={10} className="error-info-icon" />}
                            </span>
                          ) : (
                            <span className="tag completed-tag small">✓</span>
                          )}
                          <span className="network-tag small">{chain?.toUpperCase()}</span>
                          {duration && <span className="duration-badge small">{duration}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button 
                            className="btn-icon-tiny" 
                            onClick={handleCopyToForm}
                            title="Copy parameters to form"
                            style={{ marginLeft: 'auto' }}
                          >
                            <Copy size={12} />
                          </button>
                          <span className="transfer-time-small">{completedAt ? formatTimestampWithTimezone(completedAt, { timezone: activeTimezone, includeDate: false, includeTime: true, format: 'time' }) : ''}</span>
                        </div>
                      </div>
                      
                      <div className="transfer-route-single-line">
                        <span className="endpoint-name">{formatName(sourceExchange, sourceWalletInfo)}</span>
                        {sourceWalletInfo?.isMain && <span className="main-tag">Main</span>}
                        {srcAddr && (
                          <div className="addr-copy">
                            <span className="mono">{shortenAddress(srcAddr, 4, 4)}</span>
                            <button className="btn-icon-tiny" onClick={(e) => { e.stopPropagation(); copyToClipboard(srcAddr); }}><Copy size={10} /></button>
                          </div>
                        )}
                        <span className="route-arrow-small">→</span>
                        <span className="endpoint-name">{formatName(destExchange, destWalletInfo)}</span>
                        {destWalletInfo?.isMain && <span className="main-tag">Main</span>}
                        {dstAddr && (
                          <div className="addr-copy">
                            <span className="mono">{shortenAddress(dstAddr, 4, 4)}</span>
                            <button className="btn-icon-tiny" onClick={(e) => { e.stopPropagation(); copyToClipboard(dstAddr); }}><Copy size={10} /></button>
                          </div>
                        )}
                      </div>
                      
                      <div className="amounts-inline">
                        <span className="sent-amount">{parseFloat(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })} {asset} <span className="amount-label">(sent)</span></span>
                        {receivedAmount !== null && (
                          <>
                            <span className="route-arrow-small">→</span>
                            <span className="received-amount">{receivedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} {destinationAsset} <span className="amount-label">(received)</span></span>
                          </>
                        )}
                      </div>
                      
                      {/* Show balances at completion time for completed transfers */}
                      {!isFailed && completedAt && (transfer.sourceBalanceAtCompletion || transfer.destinationBalanceAtCompletion) && (
                        <div className="transfer-balances">
                          {transfer.sourceBalanceAtCompletion && (
                            <div className="balance-item">
                              <span className="balance-label">{formatName(sourceExchange, sourceWalletInfo)}:</span>
                              <span className="balance-value mono">{transfer.sourceBalanceAtCompletion}</span>
                            </div>
                          )}
                          {transfer.destinationBalanceAtCompletion && (
                            <div className="balance-item">
                              <span className="balance-label">{formatName(destExchange, destWalletInfo)}:</span>
                              <span className="balance-value mono">{transfer.destinationBalanceAtCompletion}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">No recent transfers</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h4>Confirm Transfer</h4>
            <table className="confirm-table">
              <tbody>
                <tr>
                  <td className="label">From</td>
                  <td>
                    {isTreasuryWallet(formData.sourceExchange) ? (
                      <div className="confirm-wallet-info">
                        <div className="confirm-name">
                          {sourceTreasuryWallet?.label || 'Treasury Wallet'}
                          {sourceTreasuryWallet?.name && ` - ${sourceTreasuryWallet.name}`}
                        </div>
                        <AddressWithCopy address={sourceTreasuryWallet?.address} />
                      </div>
                    ) : (
                      <div className="confirm-exchange-info">
                        <div className="confirm-name">
                          {EXCHANGES.find(e => e.id === formData.sourceExchange)?.name}
                          {formData.sourceSubAccount !== 'main' && <span className="sub-account-badge">{formData.sourceSubAccount}</span>}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                {/* Via Proxy - Show when both source and destination are exchanges */}
                {!isTreasuryWallet(formData.sourceExchange) && !isTreasuryWallet(formData.destinationExchange) && (
                  <tr className="via-treasury-row">
                    <td className="label">Via</td>
                    <td>
                      <div className="confirm-wallet-info via-treasury">
                        <div className="confirm-name">
                          <span className="via-icon">↓</span>
                          {selectedTreasuryWallet?.label || 'Proxy Wallet'}
                          {selectedTreasuryWallet?.name && ` - ${selectedTreasuryWallet.name}`}
                        </div>
                        <AddressWithCopy address={selectedTreasuryWallet?.address || formData.treasuryWallet} />
                      </div>
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="label">To</td>
                  <td>
                    {isTreasuryWallet(formData.destinationExchange) ? (
                      <div className="confirm-wallet-info">
                        <div className="confirm-name">
                          {destTreasuryWallet?.label || 'Treasury Wallet'}
                          {destTreasuryWallet?.name && ` - ${destTreasuryWallet.name}`}
                        </div>
                        <AddressWithCopy address={destTreasuryWallet?.address} />
                      </div>
                    ) : (
                      <div className="confirm-exchange-info">
                        <div className="confirm-name">
                          {availableExchanges.find(e => e.id === formData.destinationExchange)?.name || EXCHANGES.find(e => e.id === formData.destinationExchange)?.name || formData.destinationExchange}
                          {formData.destinationSubAccount !== 'main' && <span className="sub-account-badge">{formData.destinationSubAccount}</span>}
                        </div>
                        {destDepositAddress ? (
                          <AddressWithCopy address={destDepositAddress} />
                        ) : (
                          <div className="confirm-detail dim">Exchange Deposit Address</div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                <tr className="highlight">
                  <td className="label">Amount</td>
                  <td className="mono">{parseFloat(formData.amount).toLocaleString()} {formData.asset}</td>
                </tr>
                {showUsdcOnlySwapNote && (
                  <tr>
                    <td className="label">Note</td>
                    <td className="confirm-detail">{usdcOnlySwapMessage}</td>
                  </tr>
                )}
                <tr>
                  <td className="label">Network</td>
                  <td>{CHAINS.find(c => c.id === formData.chain)?.name}</td>
                </tr>
              </tbody>
            </table>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowConfirmation(false)} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransfersPage;

